/**
 * Coach Suggestion Engine — pure builder.
 *
 * Walks the persistent CoachProfile's edges + leaks, extracts the
 * one-click `actions[]` payloads that analyzers attached, dedupes them,
 * filters out accepted/rejected entries, and returns a sorted list of
 * pending Suggestion items the UI can render as "Apply / Dismiss" cards.
 *
 * Closes the observation→action loop. Coach already KNOWS what should
 * change ("VWAP appears in 80% of your reversal winners but isn't pinned
 * as recommended"); this surfaces it as a one-click apply instead of
 * making the trader navigate to the setup card and toggle it manually.
 *
 * Suggestion shape:
 *   {
 *     key: string,            // stable identity for accept/reject memory
 *     observationId: string,
 *     analyzer: string,
 *     scope: string,
 *     scopeId: string,
 *     severity: string,
 *     priority: number,
 *     // What the suggestion proposes:
 *     action: Action,         // the structured Action (type+label+payload+confirmText)
 *     // For UI rendering:
 *     title: string,          // observation title (the WHY)
 *     detail: string,         // observation detail (the EVIDENCE)
 *     status: 'pending' | 'accepted' | 'rejected'
 *   }
 *
 * Pure: takes profile + accept/reject memory, returns the list. No I/O.
 *
 * Returns the FULL list (pending + accepted + rejected) when
 * `includeResolved: true`, otherwise just pending. Default: pending only.
 */

import { ACTION_TYPE } from '../types.js';

// Action types that make sense as one-click suggestions. JUMP_TO_TRADE
// and MUTE_OBSERVATION are real action types but they're navigation /
// noise control, not "improve your process" suggestions; exclude them
// so the panel stays focused on actionable rule changes.
const SUGGESTABLE_ACTION_TYPES = new Set([
  ACTION_TYPE.PIN_CONFLUENCE_TO_SETUP,
  ACTION_TYPE.ADD_RULE_TO_SETUP,
  ACTION_TYPE.ADD_LESSON_TO_SESSION,
  ACTION_TYPE.TAG_TRADE_MISTAKE,
]);

/**
 * Build a stable suggestion key. Two observations from different runs
 * that propose the SAME change (same action type + same payload semantics)
 * should produce the same key so accept/reject memory carries forward.
 *
 * Payload normalization: stable JSON (keys sorted) so trivial reorderings
 * don't break the cache.
 */
export const buildSuggestionKey = (action) => {
  if (!action || !action.type) return '';
  const payload = action.payload || {};
  // Sorted-key stringify — bullet-proof against analyzer ordering changes
  const sortedKeys = Object.keys(payload).sort();
  const canonical = sortedKeys
    .map(k => {
      const v = payload[k];
      if (Array.isArray(v)) return `${k}=[${[...v].sort().join(',')}]`;
      if (v && typeof v === 'object') return `${k}=${JSON.stringify(v)}`;
      return `${k}=${v}`;
    })
    .join('|');
  return `${action.type}:${canonical}`;
};

/**
 * Look up status for a suggestion key from the accept/reject memory.
 */
const statusFor = (key, accepted, rejected) => {
  if (accepted && accepted[key]) return 'accepted';
  if (rejected && rejected[key]) return 'rejected';
  return 'pending';
};

/**
 * Walk one profile entry and extract Suggestion items from its actions[].
 */
const suggestionsFromEntry = (entry, accepted, rejected) => {
  if (!entry || !Array.isArray(entry.actions)) return [];
  const out = [];
  for (const action of entry.actions) {
    if (!action || !action.type) continue;
    if (!SUGGESTABLE_ACTION_TYPES.has(action.type)) continue;
    const key = buildSuggestionKey(action);
    if (!key) continue;
    out.push({
      key,
      observationId: entry.observationId || entry.id || '',
      analyzer: entry.analyzer || '',
      scope: entry.scope || '',
      scopeId: entry.scopeId || '',
      severity: entry.severity || 'info',
      priority: typeof entry.priority === 'number' ? entry.priority : 50,
      action,
      title: entry.title || '',
      detail: entry.detail || '',
      status: statusFor(key, accepted, rejected),
    });
  }
  return out;
};

/**
 * Build the suggestion list from a profile + accept/reject memory.
 *
 * @param {Object} args
 * @param {Object} args.profile             current CoachProfile (or null)
 * @param {Object} [args.accepted={}]       { suggestionKey: ISO } map of accepted decisions
 * @param {Object} [args.rejected={}]       { suggestionKey: ISO } map of rejected decisions
 * @param {boolean} [args.includeResolved=false]  include accepted+rejected in the output
 * @returns {Array<Suggestion>}             pending (or all) suggestions, sorted by priority desc
 */
export const buildSuggestions = ({
  profile = null,
  accepted = {},
  rejected = {},
  includeResolved = false,
} = {}) => {
  if (!profile) return [];

  // Walk both edges + leaks. Edges produce "lock this in" suggestions
  // (e.g., pin a winning confluence); leaks produce "fix this" ones
  // (e.g., add a rule to gate the chase pattern). Both are valid sources.
  const allEntries = [...(profile.edges || []), ...(profile.leaks || [])];
  const seen = new Map();  // dedupe by key — a single suggestion may surface from multiple entries
  for (const entry of allEntries) {
    for (const sugg of suggestionsFromEntry(entry, accepted, rejected)) {
      if (!seen.has(sugg.key) || (sugg.priority > seen.get(sugg.key).priority)) {
        seen.set(sugg.key, sugg);
      }
    }
  }
  let list = [...seen.values()];
  if (!includeResolved) list = list.filter(s => s.status === 'pending');
  list.sort((a, b) => (b.priority || 0) - (a.priority || 0));
  return list;
};

/**
 * Update accept/reject memory after a UI decision. Pure — returns a NEW
 * memory object; caller is responsible for persisting via setSettings.
 *
 * @param {'accept' | 'reject'} decision
 * @param {string} key  suggestion key (from buildSuggestionKey)
 * @param {Object} memory  { accepted: {}, rejected: {} } current state
 * @returns {Object}  new { accepted, rejected }
 */
export const recordSuggestionDecision = (decision, key, memory = {}) => {
  if (!key) return memory;
  const accepted = { ...(memory.accepted || {}) };
  const rejected = { ...(memory.rejected || {}) };
  const now = new Date().toISOString();
  if (decision === 'accept') {
    accepted[key] = now;
    delete rejected[key];   // accept supersedes prior reject
  } else if (decision === 'reject') {
    rejected[key] = now;
    delete accepted[key];   // reject supersedes prior accept
  } else if (decision === 'undo') {
    delete accepted[key];
    delete rejected[key];
  }
  return { accepted, rejected };
};

// Exports for tests
export {
  SUGGESTABLE_ACTION_TYPES,
  suggestionsFromEntry,
  statusFor,
};
